import { useCallback, useEffect, useRef, useState } from 'react'
import { Pto } from '@outcoreaix/pto'
import { useGetPersonalisedStartersQuery } from 'api/spaces-api'
import { useGetStudentPlanSectionQuery } from 'api/student-plan-api'
import { useIsDesktop } from 'hooks/useIsDesktop'
import { TOUR_SET_NAV_COLLAPSED_EVENT, TOUR_SET_NAV_DRAWER_OPEN_EVENT, TOUR_TARGET_IDS } from './tour-targets'
import { getVisibleStepDefinitions } from './build-steps'
import { useCompleteProductTour } from './useCompleteProductTour'

// Max time to wait for the "What's next" summary generation before opening the tour anyway (without
// that step). Guards against a failed/hung generation or a learner who genuinely never gets a summary.
const WHATS_NEXT_GRACE_MS = 30000

// Wait for the mobile nav Drawer's slide-in transition (~0.3s) to finish before nudging antd Tour to
// re-measure the now-visible chat-history target.
const DRAWER_SETTLE_MS = 350

const setNavCollapsed = (collapsed: boolean) => {
  window.dispatchEvent(new CustomEvent(TOUR_SET_NAV_COLLAPSED_EVENT, { detail: { collapsed } }))
}

const setNavDrawerOpen = (open: boolean) => {
  window.dispatchEvent(new CustomEvent(TOUR_SET_NAV_DRAWER_OPEN_EVENT, { detail: { open } }))
}

const rectKey = (el: HTMLElement) => {
  const { top, left, width, height } = el.getBoundingClientRect()
  return `${Math.round(top)}:${Math.round(left)}:${Math.round(width)}:${Math.round(height)}`
}

const isAnchorReady = (el: HTMLElement | null) => !!el && el.offsetHeight > 0 && !!el.querySelector('.ant-card')

export const useLearnerProductTour = (
  spaceId: number,
  spaceMember?: Pto.Spaces.SpaceMember,
  studentPlanSectionEnabled?: boolean
) => {
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState(0)
  const [showNudge, setShowNudge] = useState(false)
  const [graceExpired, setGraceExpired] = useState(false)
  // Index of the chat-history step within the displayed (DOM-filtered) step list, resolved once the
  // tour opens. Null when that step is absent. Used to drive the mobile Drawer open/closed per step.
  const [chatHistoryStepIndex, setChatHistoryStepIndex] = useState<number | null>(null)
  const hasStartedRef = useRef(false)
  const { completeProductTourIfPending } = useCompleteProductTour(spaceId)
  const isDesktop = useIsDesktop()

  // Only learners get the onboarding tour. Admins/staff are space members too, but its full-viewport
  // blocking mask (disabledInteraction) would otherwise disable the whole UI for them — including the
  // user menu — since eligibility is not otherwise role-gated.
  const isTourEligible =
    !!spaceMember &&
    spaceMember.role === Pto.Spaces.SpaceMemberRole.Learner &&
    spaceMember.productTourCompletedAt == null

  // Subscribe to the same queries that back the "Your Plan" / "What's next" section so we know when it
  // has settled. These mirror the skip conditions in YourPlanSection / PersonalisedStarters, so RTK
  // Query serves the shared cache entry without firing an extra request.
  const planEnabled = studentPlanSectionEnabled === true
  const { isLoading: isStudentPlanLoading } = useGetStudentPlanSectionQuery(spaceId, {
    skip: !spaceId || !planEnabled
  })
  const { isLoading: isPersonalisedLoading } = useGetPersonalisedStartersQuery(spaceId, {
    skip: !spaceId || planEnabled || !spaceMember?.summary
  })
  // "What's next" only renders once the learner summary is generated (async, during/after onboarding).
  // While it is still null we are waiting on generation, not looking at a genuinely empty section — so
  // the tour must keep waiting rather than open early and fall back to Explore without that step.
  const isWhatsNextPending = !planEnabled && !!spaceMember && spaceMember.summary == null
  // The active section's query is settled (the skipped one reports isLoading=false).
  const isPlanSectionSettled = !isStudentPlanLoading && !isPersonalisedLoading && !isWhatsNextPending

  // Never wait forever: once eligible, if the section has not settled within the grace window, open the
  // tour anyway (the Explore fallback kicks in, so the tour just omits the What's next step).
  useEffect(() => {
    if (!isTourEligible || isPlanSectionSettled || hasStartedRef.current) return

    const timeoutId = window.setTimeout(() => setGraceExpired(true), WHATS_NEXT_GRACE_MS)

    return () => clearTimeout(timeoutId)
  }, [isTourEligible, isPlanSectionSettled])

  useEffect(() => {
    // Start the tour once. The guard also prevents it from reopening while the
    // "complete product tour" mutation is still invalidating the member query after a skip.
    if (!isTourEligible || open || hasStartedRef.current) return

    // Wait for the "Your Plan" / "What's next" section to finish loading (including summary generation)
    // before opening. Otherwise the Explore fallback below can open the tour mid-load, capturing a step
    // set without the first step — and since buildSteps() runs once at open, the tour would stay stuck
    // at the wrong step count. The grace timeout lets it proceed if generation never completes.
    if (!isPlanSectionSettled && !graceExpired) return

    // Desktop only: expand the chat sidebar up front (the tour highlights chat history at step 3),
    // then wait for the layout to settle before opening. Expanding after the tour opens animates the
    // sidebar from 92px to 300px, shifting the page while the first step's mask is already measured —
    // which left the highlight misaligned until an antd re-measure (e.g. Next then Back) fixed it.
    // On mobile the chat history lives in a Drawer that stays closed until its step, so there is no
    // sidebar to expand here.
    if (isDesktop) setNavCollapsed(false)

    let rafId = 0
    let timeoutId = 0
    let lastRect: string | null = null
    let stableFrames = 0

    // Which laid-out section to gate the tour's opening on. Normally the "Your Plan" / "What's next"
    // section (step 1). But that section renders nothing when there are no activities/starters — and
    // since we only reach here once it has settled (isPlanSectionSettled), an absent anchor means it is
    // genuinely empty, so we fall back to the Explore section. buildSteps() then drops the missing first
    // step and the tour just starts at step 2.
    const pickAnchor = () => {
      const yourPlan = document.getElementById(TOUR_TARGET_IDS.yourPlan)
      if (isAnchorReady(yourPlan)) return yourPlan

      if (!yourPlan) {
        const explore = document.getElementById(TOUR_TARGET_IDS.exploreActivities)
        if (isAnchorReady(explore)) return explore
      }

      return null
    }

    const waitForStableTarget = () => {
      const anchor = pickAnchor()
      if (anchor) {
        const key = rectKey(anchor)
        if (key === lastRect) {
          // Two consecutive identical frames means the sidebar's width transition has finished. Also
          // require the chat-history anchor to be in the DOM before opening: it lives in a portal (the
          // desktop sidebar / the mobile forceRender drawer) that can mount a frame later than the page
          // content, and buildSteps() runs once at open — so opening before it exists would permanently
          // drop that step (the tour would show 4 steps instead of 5). It is always mounted eventually,
          // so this never hangs; it just waits a frame or two more when needed.
          stableFrames += 1
          if (stableFrames >= 2 && document.getElementById(TOUR_TARGET_IDS.chatHistory)) {
            hasStartedRef.current = true
            // Resolve the chat-history step's index now that the layout (and its target) is settled,
            // so the per-step effect below can open the mobile Drawer on exactly that step.
            const visibleSteps = getVisibleStepDefinitions(studentPlanSectionEnabled)
            const chatIdx = visibleSteps.findIndex(({ targetId }) => targetId === TOUR_TARGET_IDS.chatHistory)
            setChatHistoryStepIndex(chatIdx >= 0 ? chatIdx : null)
            setOpen(true)
            return
          }
        } else {
          stableFrames = 0
          lastRect = key
        }
      } else {
        stableFrames = 0
        lastRect = null
      }
      rafId = requestAnimationFrame(waitForStableTarget)
    }

    // Desktop: let the sidebar's expand animation (antd Sider width transition, ~0.2s) fully finish
    // before we start measuring. Measuring during the transition — or in the few frames before it even
    // starts — captures the collapsed-layout position, so the first step's mask lands misaligned.
    // Mobile: no sidebar animation runs, so start measuring immediately.
    if (isDesktop) {
      timeoutId = window.setTimeout(() => {
        rafId = requestAnimationFrame(waitForStableTarget)
      }, 350)
    } else {
      rafId = requestAnimationFrame(waitForStableTarget)
    }

    return () => {
      clearTimeout(timeoutId)
      cancelAnimationFrame(rafId)
    }
  }, [isTourEligible, open, isPlanSectionSettled, graceExpired, isDesktop, studentPlanSectionEnabled])

  // Mobile only: reveal the chat history (in the nav Drawer) exactly on its step, and hide it again on
  // every other step. Opening after the step is active means antd Tour first measures the still-hidden
  // target, so once the Drawer's slide-in settles we fire a resize to make antd re-measure and realign.
  useEffect(() => {
    if (!open || isDesktop || chatHistoryStepIndex == null) return

    const shouldReveal = current === chatHistoryStepIndex
    setNavDrawerOpen(shouldReveal)
    if (!shouldReveal) return

    const timeoutId = window.setTimeout(() => window.dispatchEvent(new Event('resize')), DRAWER_SETTLE_MS)

    return () => clearTimeout(timeoutId)
  }, [open, isDesktop, current, chatHistoryStepIndex])

  // Collapse the sidebar back / close the mobile Drawer when the tour ends by any path: skip
  // (open -> false) or navigating away by clicking the final activity card, which unmounts this hook
  // and runs the cleanup.
  useEffect(() => {
    if (!open) return

    return () => {
      setNavCollapsed(true)
      setNavDrawerOpen(false)
    }
  }, [open])

  const handleSkip = useCallback(() => {
    completeProductTourIfPending()
    setOpen(false)
    setShowNudge(true)
  }, [completeProductTourIfPending])

  const dismissNudge = useCallback(() => setShowNudge(false), [])

  return { isTourEligible, open, current, setCurrent, handleSkip, showNudge, dismissNudge, isDesktop }
}
